import type { ComponentProps, FC } from 'react'
import type { NotificationMode } from '@opallabs/concepts'
import type { GemIconName } from '@opallabs/constellation'
import { Button2, DropdownMenu, Icon } from '@opallabs/lapidary'
import * as styles from './styles'

const MODE_OPTIONS: Record<
  NotificationMode,
  { description: string; glyph: GemIconName; label: string }
> = {
  manual: {
    label: 'Manual',
    description: 'Hold until triggered to users with access',
    glyph: 'gem-bell',
  },
  automatic: {
    label: 'Automatic',
    description: 'Send immediately to users with access',
    glyph: 'gem-bell',
  },
  off: {
    label: 'Off',
    description: 'Do not send notifications',
    glyph: 'gem-bell-hidden',
  },
}

const MIXED_OPTION = {
  label: 'Multiple values',
  description: 'Notify according to current settings',
  glyph: 'gem-bell',
} satisfies (typeof MODE_OPTIONS)[NotificationMode]

const ModeItem: FC<
  Readonly<{
    isChecked: boolean
    onSelect: () => void
    option: (typeof MODE_OPTIONS)[NotificationMode]
  }>
> = ({ isChecked, onSelect, option }) => (
  <DropdownMenu.Item
    aria-checked={isChecked}
    css={styles.modeOption}
    onSelect={() => {
      if (!isChecked) onSelect()
    }}
    role="menuitemradio"
  >
    <Icon ariaHidden css={styles.modeOptionIcon} glyph={option.glyph} />
    <span css={styles.modeOptionText}>
      <span css={styles.modeOptionLabel}>{option.label}</span>
      <span css={styles.modeOptionDescription}>{option.description}</span>
    </span>
  </DropdownMenu.Item>
)

/**
 * Picks how a People Picker sends notifications. Use `mixed` when the values
 * disagree, and pass `onMixedSelect` so the user can undo a staged pick.
 *
 * Renders in place, not portalled or modal, so opening it doesn't close or
 * hide the picker it sits in.
 */
export const NotificationModeMenu: FC<
  Readonly<{
    mode: NotificationMode | 'mixed'
    onMixedSelect?: () => void
    onModeChange: (mode: NotificationMode) => void
    variant?: ComponentProps<typeof Button2>['variant']
  }>
> = ({ mode, onMixedSelect, onModeChange, variant = 'secondary' }) => (
  <DropdownMenu.Root modal={false}>
    <DropdownMenu.Trigger asChild>
      <Button2 size="small" variant={variant}>
        <Button2.Icon
          glyph={
            mode === 'mixed' ? MIXED_OPTION.glyph : MODE_OPTIONS[mode].glyph
          }
        />
        <Button2.Label>
          {mode === 'mixed' ? MIXED_OPTION.label : MODE_OPTIONS[mode].label}
        </Button2.Label>
        <Button2.Icon glyph="gem-chevron-s" />
      </Button2>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="start">
      {onMixedSelect && (
        <ModeItem
          isChecked={mode === 'mixed'}
          onSelect={onMixedSelect}
          option={MIXED_OPTION}
        />
      )}
      {Object.entries(MODE_OPTIONS).map(([value, option]) => (
        <ModeItem
          isChecked={value === mode}
          key={value}
          onSelect={() => onModeChange(value as NotificationMode)}
          option={option}
        />
      ))}
    </DropdownMenu.Content>
  </DropdownMenu.Root>
)
NotificationModeMenu.displayName =
  'Satellite: NotificationModeMenu ← CustomFields'
